/**
 * Display-name mappings for plan tiers and audience tiers.
 *
 * IMPORTANT: These names mirror the canonical mapping owned by the backend's
 * `BillingConfigService` (see `apps/backend/src/billing/billing-config.service.ts`).
 * Internal slugs (`trial`/`starter`/`pro`/`enterprise`/`locked_free` for plan tiers,
 * `tier1`/`tier2`/`tier3` for audience tiers) MUST NEVER change. UI copy lives
 * here and on the backend constant; keep both in sync.
 *
 * Once the backend exposes `GET /billing/display-names` (TBD), this file should
 * proxy that response via SWR instead of duplicating the strings.
 */

import i18n from '@/i18n'

import type { SubscriptionTier } from "@/lib/api";

export const PLAN_TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  trial: "Trial",
  partner_trial: "Partner trial",
  vip_trial: "VIP trial",
  pro_trial: "Pro Trial",
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
  fairgen_internal: "Fairgen Internal",
  locked_free: "Free",
};

export function planTierDisplayName(
  tier: SubscriptionTier | string | null | undefined,
): string {
  if (!tier) {
    return i18n.t("billing.tiers.locked_free", { ns: "app" });
  }
  const key = `billing.tiers.${tier}` as const;
  const translated = i18n.t(key, { ns: "app", defaultValue: (PLAN_TIER_DISPLAY_NAMES as Record<string, string>)[tier] ?? tier });
  if (translated === key) {
    return (PLAN_TIER_DISPLAY_NAMES as Record<string, string>)[tier] ?? tier;
  }
  return translated;
}

// Audience tier display names moved to `@/lib/billing/display-names` so
// non-billing surfaces (audience cards) can import without reaching into a
// settings path. Re-exported here for backward compatibility with existing
// callers.
export {
  AUDIENCE_TIER_DISPLAY_NAMES,
  audienceTierDisplayName,
} from "@/lib/billing/display-names";
